import styled from 'styled-components';
import { SongListType } from '../../../../../common/src/model.ts';
import { staffCanEditEvents } from '../../../../../common/src/org.ts';
import BackArrowIcon from '../../../assets/arrow-previous-left-icon.svg?react';
import { alert } from '../../../util/confirm.tsx';
import { ContactSupportButton } from '../../../util/shared.tsx';

export const MinSongListRowCount = 15;
export const SongRowWidth = 'min(calc(100vw - 4px), 300px)';
export const SongRowHeightDouble = '4rem'; // mobile (to stack label above song/text)
export const SongRowHeightSingle = '2.5rem'; // desktop
export const PlannerYearHeight = '60px'; // for sticky positioning (yuck)
export const SongCellPadding = '10px';
export const DateRowTop = `calc(${PlannerYearHeight} + 1px)`;
export const PlannerButtonIconThreshold = '800px'; // screen width at which planner buttons switch between icons/text

export const BackButton = ({ onClick, isAbsolutePosition }: { onClick: () => void; isAbsolutePosition?: boolean }) => {
  return (
    <BackButtonWrapper onClick={onClick} $isAbsolutePosition={isAbsolutePosition}>
      <BackArrowIconWrapper />
    </BackButtonWrapper>
  );
};

export const BackButtonWidth = '35px';
export const BackButtonHeight = '55px';

// takes up space in the flow where the back button would have resided
export const BackButtonPlaceholder = styled.div`
  pointer-events: none;
  width: ${BackButtonWidth};
  height: ${BackButtonHeight};
`;

const BackButtonWrapper = styled.div<{ $isAbsolutePosition?: boolean }>`
  position: ${(props) => (props.$isAbsolutePosition ? 'absolute' : 'fixed')};
  top: 0;
  left: 0;
  padding: 17px 11px;
  cursor: pointer;
  background-color: var(--color-background);
`;

const BackArrowIconWrapper = styled(BackArrowIcon)`
  width: 15px;
  fill: var(--color-text);
`;

export async function DisplayUnauthorizedAlert() {
  await alert({
    confirmation: (
      <span>
        Your are not
        <br />
        authorized to make changes.
        <br />
        <br />
        <ContactSupportButton small={true} />
      </span>
    ),
  });
}

export const YearHeader = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.7rem;
  max-width: 85vw;
  text-wrap: nowrap;
  overflow: hidden;
  background-color: var(--color-background);
`;

export const menuBorderRadius = '10px';

// Must be child of div that opens it (onClick) and closes it (onBlur)
// Parent must have a tabIndex to for onBlur to work properly.
// Parent must not be a flexbox for proper positioning. It needs no position: relative for a Menu that hangs below it,
// but does for one that opens upward (see useOpensUpward in popup.tsx), as TypeIconWrapper and RepeatsEntryWrapper are
export const Menu = styled.div`
  z-index: 8;
  position: absolute;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-text);
  border-radius: ${menuBorderRadius};
  background-color: var(--color-background);
  width: max-content;
`;

export function getVerticalMenuPadding(border?: boolean) {
  return border ? '9px' : '5px';
}

export const MenuEntry = styled.div<{
  $isFirst: boolean;
  $isLast: boolean;
  $borderTop?: boolean;
  $borderBottom?: boolean;
}>`
  text-align: left;
  cursor: pointer;
  display: flex;
  padding: ${(props) => getVerticalMenuPadding(props.$borderTop)} 15px
    ${(props) => getVerticalMenuPadding(props.$borderBottom)} 12px;
  gap: 10px;
  align-items: center;
  ${(props) =>
    props.$borderTop &&
    `
    border-top: 1px solid var(--color-text);
  `}
  // avoid clipping rounded menu border, give a little breathing room to top & bottom of menu
  ${(props) =>
    props.$isFirst &&
    `
    border-top-left-radius: ${menuBorderRadius};
    border-top-right-radius: ${menuBorderRadius};
    padding-top: 8px;
  `}
  ${(props) =>
    props.$isLast &&
    `
    border-bottom-left-radius: ${menuBorderRadius};
    border-bottom-right-radius: ${menuBorderRadius};
    padding-bottom: 8px;
  `}
  &:hover {
    background: var(--color-background-selected-item);
  }
`;
export const PlannerHeaderButtonGap = '10px';

export function isAuthorizedToEditSongList({
  isOrgAdmin,
  isInternalUser,
  isStaff,
  songListType,
}: {
  isOrgAdmin: boolean;
  isInternalUser: boolean;
  isStaff?: boolean;
  songListType: SongListType;
}) {
  return isOrgAdmin || isInternalUser || (isStaff && staffCanEditEvents && songListType === SongListType.Event);
}
